/**
 * Mock only: the texts the Comeback Loop would send, as a phone's message
 * thread, so the loop can be walked without a phone. Reads the run as it
 * stands (the case sealed, missions filed) and lists what would go out and
 * when; each text's link does here what it would do on a phone (`?go=`),
 * and a link opened days later arrives as a comeback (the doors say so).
 * Shown with the flag, like the dev jump.
 */
import { useState } from "react";
import { MessageSquareText, X } from "lucide-react";
import { useFunnelStore } from "@/store";
import { useStepperStore } from "@/store/stepper";
import { useMissionFile } from "@/features/funnel/api/use-mission-file";
import { engravedName, useMemberName } from "./steptypes/operative-name";
import { textsFor, whenText } from "./comeback-texts";

/** A link opened from a later text: the doors greet them as back. */
export const COMEBACK_KEY = "sm-comeback";

const DAY_MS = 86_400_000;

export function ComebackInbox() {
  const [open, setOpen] = useState(false);
  const sealed = useFunnelStore((s) => s.sealed);
  const handler = useFunnelStore((s) => s.handler);
  const goTo = useStepperStore((s) => s.goTo);
  const { data: filed = [] } = useMissionFile();
  const { data: memberName } = useMemberName();
  const name = engravedName() || memberName || "Operative";
  // the clock, read when the thread is opened (so the "when" lines hold
  // while it is read)
  const [now, setNow] = useState(() => Date.now());
  const toggle = () => {
    setNow(Date.now());
    setOpen((o) => !o);
  };
  const times = filed
    .map((m) => (m.filedAt ? new Date(m.filedAt).getTime() : NaN))
    .filter((t) => !Number.isNaN(t));
  const lastFiledAt = times.length ? Math.max(...times) : null;
  const filedToday = times.filter((t) => now - t < DAY_MS).length;
  const texts = textsFor({
    name,
    handler,
    sealed,
    filedCount: filed.length,
    lastFiledAt,
    filedToday,
    now,
  });
  const follow = (go: string, at: number) => {
    const days = Math.max(0, Math.round((at - now) / DAY_MS));
    try {
      if (days >= 1) sessionStorage.setItem(COMEBACK_KEY, String(days));
    } catch {
      /* private mode */
    }
    setOpen(false);
    goTo(go);
  };

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="fixed right-3 bottom-3 z-[100] flex cursor-pointer items-center gap-1.5 rounded-md border border-white/25 bg-black/70 px-2.5 py-1 font-mono text-[10px] tracking-[.14em] text-white/80 uppercase hover:bg-black/90"
      >
        <MessageSquareText className="size-3.5" />
        Texts
        {texts.length ? (
          <span className="rounded-full bg-[#d6a94e] px-1.5 text-[9px] font-bold text-black">
            {texts.length}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Texts the agency would send"
          className="fixed right-3 bottom-11 z-[100] flex max-h-[72dvh] w-[300px] flex-col overflow-hidden rounded-[18px] border border-white/15 bg-[#0e0e10] font-sans text-[#f4eddb] shadow-[0_18px_50px_rgba(0,0,0,.7)]"
        >
          <div className="flex items-center justify-between border-b border-white/10 px-3.5 py-2.5">
            <div>
              <div className="text-[13px] font-semibold">Secret Missions</div>
              <div className="font-mono text-[9.5px] tracking-[.18em] text-white/45 uppercase">
                Mock inbox · what would be sent
              </div>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="grid size-8 cursor-pointer place-items-center rounded-full bg-white/10"
            >
              <X className="size-4" />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3">
            {texts.length === 0 ? (
              <p className="px-1 py-4 text-center text-[13px] leading-snug text-white/55">
                Nothing to send yet. File a mission and create tomorrow's case:
                the texts follow from that.
              </p>
            ) : (
              texts.map((t) => (
                <div key={t.id} className="flex flex-col items-start gap-1">
                  <span className="px-1 font-mono text-[9.5px] tracking-[.14em] text-white/40 uppercase">
                    {whenText(t.at, now)}
                  </span>
                  <div className="max-w-[92%] rounded-[16px] rounded-bl-[4px] bg-[#26232a] px-3 py-2 text-[13.5px] leading-[1.4]">
                    {t.body}
                    {t.go ? (
                      <button
                        type="button"
                        onClick={() => follow(t.go!, t.at)}
                        className="mt-1.5 block cursor-pointer text-[13px] text-[#8fbfe8] underline underline-offset-2"
                      >
                        secretmissions.com/?go={encodeURIComponent(t.go)}
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="border-t border-white/10 px-3.5 py-2 font-mono text-[9.5px] leading-snug tracking-[.06em] text-white/40">
            Reminders only. Nothing here asks for a reply: questions go to the
            webchat.
          </div>
        </div>
      ) : null}
    </>
  );
}
